import {useState, useCallback, useRef} from 'react';
import {
    fetchMyUserAccount,
    fetchMyEmployee,
    fetchMyLeaveBalance,
    fetchMyLeaveRequests,
    fetchMySettings,
    fetchMyWorkTimeBalance,
    fetchMyPendingApprovals
} from '../api/MeApi';
import {Employee, AnnualLeaveBalance, WorkTimeBalance} from '../api/EmployeeApi';
import {LeaveRequest} from '../api/RequestApi';
import {UserAccount} from '../api/UserAccountApi';
import {UserSettings} from '../api/SettingsApi';
import {ApiError, ApiResponse, ErrorDetail} from '../api/types';
import {ApprovalInstance} from "../api/ApprovalApi";

// ============================================================================
// Types
// ============================================================================

interface LazyResourceState<TData> {
    data: TData | null;
    loading: boolean;
    error: ErrorDetail | null;
    loaded: boolean;
}

interface UseMeReturn {
    // User Account
    userAccount: LazyResourceState<UserAccount>;
    getUserAccount: () => Promise<UserAccount | null>;
    refetchUserAccount: () => Promise<UserAccount | null>;

    // Employee
    employee: LazyResourceState<Employee>;
    getEmployee: () => Promise<Employee | null>;
    refetchEmployee: () => Promise<Employee | null>;

    // Leave Balance
    workTimeBalances: LazyResourceState<WorkTimeBalance[]>;
    getWorkTimeBalances: () => Promise<WorkTimeBalance[] | null>;
    refetchWorkTimeBalances: () => Promise<WorkTimeBalance[] | null>;

    leaveBalance: LazyResourceState<AnnualLeaveBalance>;
    getLeaveBalance: () => Promise<AnnualLeaveBalance | null>;
    refetchLeaveBalance: () => Promise<AnnualLeaveBalance | null>;

    // Leave Requests
    leaveRequests: LazyResourceState<LeaveRequest[]>;
    getLeaveRequests: () => Promise<LeaveRequest[] | null>;
    refetchLeaveRequests: () => Promise<LeaveRequest[] | null>;

    // Settings
    settings: LazyResourceState<UserSettings>;
    getSettings: () => Promise<UserSettings | null>;
    refetchSettings: () => Promise<UserSettings | null>;

    // Pending Approvals
    pendingApprovals: LazyResourceState<ApprovalInstance[]>;
    getPendingApprovals: () => Promise<ApprovalInstance[] | null>;
    refetchPendingApprovals: () => Promise<ApprovalInstance[] | null>;

    // Utilities
    clearCache: () => void;
    isLoading: boolean;
    hasErrors: boolean;
}

// ============================================================================
// Lazy Resource Hook
// ============================================================================

/**
 * Creates a lazy-loaded resource that only fetches when explicitly requested.
 * Follows the same pattern as useResource but with lazy loading.
 */
function useLazyResource<TData>(
    fetcher: () => Promise<ApiResponse<TData>>
) {

    const [state, setState] = useState<LazyResourceState<TData>>({
        data: null,
        loading: false,
        error: null,
        loaded: false
    });

    const isMountedRef = useRef(true);
    const loadingRef = useRef(false);

    // Cleanup on unmount
    useCallback(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const load = useCallback(
        async (force: boolean = false): Promise<TData | null> => {
            if (!force && state.loaded && state.data !== null) return state.data;
            if (loadingRef.current) return state.data;
            if (!isMountedRef.current) return null;

            loadingRef.current = true;
            setState(prev => ({...prev, loading: true, error: null}));

            try {
                const response = await fetcher();
                if (!isMountedRef.current) return null;
                if (response.error) throw new ApiError(response.error);

                const data = response.data as TData;
                setState({ data, loading: false, error: null, loaded: true });
                return data;
            } catch (err) {
                if (!isMountedRef.current) return null;
                const error = err as ErrorDetail;
                setState(prev => ({ ...prev, loading: false, error }));
                console.error('Lazy resource loading error:', err);
                return null;
            } finally {
                loadingRef.current = false;
            }
        },
        [fetcher, state.loaded, state.data]
    );

    const get = useCallback(() => load(false), [load]);
    const refetch = useCallback(() => load(true), [load]);
    const reset = useCallback(() => {
        setState({ data: null, loading: false, error: null, loaded: false });
    }, []);

    return { state, get, refetch, reset };
}

// ============================================================================
// Main Hook: useMe
// ============================================================================

export const useMe = (): UseMeReturn => {
    const userAccountResource = useLazyResource(fetchMyUserAccount);
    const employeeResource = useLazyResource(fetchMyEmployee);
    const workTimeBalanceResource = useLazyResource(fetchMyWorkTimeBalance);
    const leaveBalanceResource = useLazyResource(fetchMyLeaveBalance);
    const leaveRequestsResource = useLazyResource(fetchMyLeaveRequests);
    const settingsResource = useLazyResource(fetchMySettings);
    const pendingApprovalsResource = useLazyResource(fetchMyPendingApprovals);

    const clearCache = useCallback(() => {
        userAccountResource.reset();
        employeeResource.reset();
        workTimeBalanceResource.reset();
        leaveBalanceResource.reset();
        leaveRequestsResource.reset();
        settingsResource.reset();
        pendingApprovalsResource.reset();
    }, [
        userAccountResource,
        employeeResource,
        workTimeBalanceResource,
        leaveBalanceResource,
        leaveRequestsResource,
        settingsResource,
        pendingApprovalsResource
    ]);

    const isLoading = [
        userAccountResource.state.loading,
        employeeResource.state.loading,
        leaveBalanceResource.state.loading,
        leaveRequestsResource.state.loading,
        settingsResource.state.loading,
        pendingApprovalsResource.state.loading
    ].some(Boolean);

    const hasErrors = [
        userAccountResource.state.error,
        employeeResource.state.error,
        leaveBalanceResource.state.error,
        leaveRequestsResource.state.error,
        settingsResource.state.error,
        pendingApprovalsResource.state.error
    ].some(error => error !== null);

    return {
        // User Account
        userAccount: userAccountResource.state,
        getUserAccount: userAccountResource.get,
        refetchUserAccount: userAccountResource.refetch,

        // Employee
        employee: employeeResource.state,
        getEmployee: employeeResource.get,
        refetchEmployee: employeeResource.refetch,

        // Work Time Balances
        workTimeBalances: workTimeBalanceResource.state,
        getWorkTimeBalances: workTimeBalanceResource.get,
        refetchWorkTimeBalances: workTimeBalanceResource.refetch,

        // Leave Balance
        leaveBalance: leaveBalanceResource.state,
        getLeaveBalance: leaveBalanceResource.get,
        refetchLeaveBalance: leaveBalanceResource.refetch,

        // Leave Requests
        leaveRequests: leaveRequestsResource.state,
        getLeaveRequests: leaveRequestsResource.get,
        refetchLeaveRequests: leaveRequestsResource.refetch,

        // Settings
        settings: settingsResource.state,
        getSettings: settingsResource.get,
        refetchSettings: settingsResource.refetch,

        // Pending Approvals
        pendingApprovals: pendingApprovalsResource.state,
        getPendingApprovals: pendingApprovalsResource.get,
        refetchPendingApprovals: pendingApprovalsResource.refetch,

        // Utilities
        clearCache,
        isLoading,
        hasErrors
    };
};